// src/pages/markets/board/card/CardTrendChart.tsx
//
// Deliberately not ConvergenceChart: that one carries zoom, drag-pan,
// fullscreen, trade/signal/alert overlays and needs Firebase stubbed in tests.
// Detail analysis lives in the EventView; this is a shape at a glance.
//
// The SVG scales non-uniformly through preserveAspectRatio, so the path uses a
// non-scaling stroke and every label is HTML on top rather than <text> inside.

import { numSx } from '../../../../components/common/page/numberStyles';
import { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import type { SeriesPoint } from '../api/boardTypes';
import { formatProb } from './cardFormat';
import { VB, buildGeometry, type Geometry } from './trendGeometry';

export interface CardTrendChartProps {
    points: SeriesPoint[];
    referenceValue: number | null;
    referenceColor: string;
    windowLabel: string;
    /** Values that must stay inside the drawn range — entry prices, mostly. */
    extraValues?: number[];
    /** The big last-value badge. Off when something above already prints it. */
    showLast?: boolean;
    /** Absolutely-positioned HTML on top of the line, given the same scale. */
    overlay?: (geo: Geometry) => React.ReactNode;
}

export function CardTrendChart({
    points,
    referenceValue,
    referenceColor,
    windowLabel,
    extraValues,
    showLast = true,
    overlay,
}: CardTrendChartProps) {
    const theme = useTheme();
    const geo = useMemo(
        () => buildGeometry(points, referenceValue, extraValues),
        [points, referenceValue, extraValues]
    );

    if (!geo) {
        return (
            <Box
                sx={{
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '6px',
                    border: '1px dashed',
                    borderColor: 'divider',
                }}
            >
                <Typography sx={{ fontSize: '11px', color: 'text.disabled' }}>
                    Not enough price history
                </Typography>
            </Box>
        );
    }

    const lineColor = theme.palette.primary.main;

    return (
        <Box sx={{ height: '100%', position: 'relative' }}>
            <svg
                viewBox={`0 0 ${VB} ${VB}`}
                preserveAspectRatio="none"
                style={{ width: '100%', height: '100%', display: 'block' }}
            >
                <path d={geo.area} fill={alpha(lineColor, 0.14)} stroke="none" />
                <path
                    d={geo.path}
                    fill="none"
                    stroke={lineColor}
                    strokeWidth={1.5}
                    strokeLinejoin="round"
                    vectorEffect="non-scaling-stroke"
                />
                {geo.referenceY !== null && (
                    <line
                        x1={0}
                        x2={VB}
                        y1={geo.referenceY}
                        y2={geo.referenceY}
                        stroke={referenceColor}
                        strokeWidth={1}
                        strokeDasharray="3 3"
                        vectorEffect="non-scaling-stroke"
                    />
                )}
            </svg>

            {showLast && (
                <Typography
                    sx={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        fontSize: '12px',
                        fontWeight: 700,
                        ...numSx,
                        color: 'text.primary',
                        bgcolor: alpha(theme.palette.background.paper, 0.8),
                        px: 0.5,
                        borderRadius: '4px',
                    }}
                >
                    {formatProb(geo.last)}
                </Typography>
            )}

            {referenceValue !== null && (
                <Typography
                    sx={{
                        position: 'absolute',
                        left: 0,
                        top: `${(geo.referenceY ?? 0)}%`,
                        transform: 'translateY(-100%)',
                        fontSize: '10px',
                        color: referenceColor,
                        bgcolor: alpha(theme.palette.background.paper, 0.8),
                        px: 0.25,
                        borderRadius: '3px',
                        pointerEvents: 'none',
                    }}
                >
                    implied {formatProb(referenceValue)}
                </Typography>
            )}

            <Typography
                sx={{
                    position: 'absolute',
                    left: 0,
                    bottom: 0,
                    fontSize: '10px',
                    color: 'text.disabled',
                    bgcolor: alpha(theme.palette.background.paper, 0.8),
                    px: 0.25,
                }}
            >
                {windowLabel}
            </Typography>

            {overlay?.(geo)}
        </Box>
    );
}